import { type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { MAIN_SITE_URL } from '@/mocks/site';
import { categories } from '@/mocks/categories';

const COLLAGE_KEYS = ['shoes', 'hoodies', 'bags', 'perfumes'];

export default function Hero() {
  const { t } = useTranslation();

  const collage = categories.filter((c) => COLLAGE_KEYS.includes(c.key));

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' });
  };

  const trustItems = [
    { icon: 'ri-layout-grid-line', label: t('home.hero.trust1') },
    { icon: 'ri-refresh-line', label: t('home.hero.trust2') },
    { icon: 'ri-translate-2', label: t('home.hero.trust3') },
    { icon: 'ri-shield-check-line', label: t('home.hero.trust4') },
  ];

  return (
    <section className="relative overflow-hidden bg-background-50">
      {/* decorative soft blobs */}
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary-100/60 blur-3xl" />
      <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-accent-100/60 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-2 lg:gap-14">
        {/* Left */}
        <div className="flex flex-col items-start">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-800">
            <i className="ri-sparkling-2-line text-sm" />
            {t('home.hero.badge')}
          </span>

          <h1 className="mt-5 font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-4xl lg:text-[2.75rem]">
            {t('home.hero.h1')}
          </h1>

          <p className="mt-4 max-w-xl text-base leading-relaxed text-foreground-600">{t('home.hero.subtitle')}</p>

          {/* Search */}
          <form onSubmit={handleSearch} className="mt-6 flex w-full max-w-md items-center gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
              <input
                type="text"
                placeholder={t('common.search.placeholder')}
                className="w-full rounded-lg border border-background-200 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <button
              type="submit"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              aria-label="Search"
            >
              <i className="ri-arrow-right-line text-lg" />
            </button>
          </form>

          {/* CTAs */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              {t('common.cta.explore')}
              <i className="ri-arrow-right-up-line text-base" />
            </a>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-900 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              {t('common.cta.spreadsheet')}
            </a>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-600"
            >
              {t('common.cta.buyNow')}
              <i className="ri-shopping-bag-3-line text-base" />
            </a>
          </div>

          {/* Trust strip */}
          <div className="mt-8 grid w-full max-w-xl grid-cols-2 gap-3 sm:grid-cols-4">
            {trustItems.map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className={`${item.icon} text-sm`} />
                </span>
                <span className="text-xs font-medium text-foreground-700">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right collage */}
        <div className="relative">
          <div className="grid grid-cols-2 gap-4">
            {collage.map((c, i) => (
              <div
                key={c.key}
                className={`overflow-hidden rounded-lg border border-background-200 bg-background-100 ${i % 2 === 1 ? 'translate-y-4' : ''}`}
              >
                <div className="h-40 w-full md:h-48">
                  <img
                    src={c.image}
                    alt={t(`categories.${c.key}`)}
                    title={`${t(`categories.${c.key}`)} - USFans Spreadsheet`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="px-3 py-2.5">
                  <p className="truncate text-xs font-medium text-foreground-700">{t(`categories.${c.key}`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}